// PipelineBoard — 11-stage operational kanban backed by /api/pipeline/board + /report.
// Click a card to open the lead drawer. Manual stage transitions happen inside the drawer.
import { useEffect, useState } from "react";
import { Layers, RefreshCw, Users } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const RED = "var(--rx-red, #E11D2A)";

const stageColor = (i, total) => {
  // 0=red, mid=amber, last=green. Smooth blend.
  if (i === total - 1) return "#10B981"; // Closed Won / final
  const ratio = i / (total - 1);
  if (ratio < 0.5) return RED;
  return "#F59E0B";
};

const fmtSeconds = (s) => {
  if (!s || s < 1) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
};

export default function PipelineBoard({ onPickLead }) {
  const [board, setBoard] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, r] = await Promise.all([
        fetch(`${API}/pipeline/board`).then((res) => res.json()),
        fetch(`${API}/pipeline/report`).then((res) => res.json()),
      ]);
      setBoard(b);
      setReport(r);
    } catch (e) {
      console.error("Pipeline load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !board) {
    return <div className="text-white/55 text-[12px]" data-testid="pipeline-loading">Loading pipeline…</div>;
  }
  if (!board) {
    return <div className="text-white/55 text-[12px]" data-testid="pipeline-empty">No pipeline data.</div>;
  }

  const { stages, columns, counts } = board;
  const totalLeads = report?.total_leads || 0;

  return (
    <div className="space-y-5" data-testid="pipeline-board">
      {/* Header / Summary */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-heavy text-[16px] inline-flex items-center gap-2">
            <Layers size={14} style={{ color: RED }} /> PIPELINE
          </p>
          <p className="text-[11px] tracking-[0.14em] font-bold text-white/45 mt-1">
            {totalLeads} total leads · {stages.length} stages
          </p>
        </div>
        <button
          onClick={load}
          data-testid="pipeline-refresh"
          className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] font-bold text-white/65 hover:text-white border border-white/15 px-3 py-1.5"
        >
          <RefreshCw size={12} /> REFRESH
        </button>
      </div>

      {/* Conversion bar */}
      {report && (
        <div className="rounded-sm border border-white/10 bg-black/40 px-4 py-3" data-testid="pipeline-conversion">
          <p className="text-[10px] tracking-[0.16em] font-bold text-white/55 mb-2.5">CONVERSION (reached or past)</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-[11px]">
            {stages.filter((s) => s !== "Closed Lost").map((s, i) => {
              const pct = report.conversion_rate_pct?.[s] ?? 0;
              const avg = report.avg_time_in_stage_seconds?.[s] ?? 0;
              return (
                <div key={s} className="flex flex-col" data-testid={`conv-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                  <p className="text-white/80 truncate font-bold">{s}</p>
                  <p className="text-[16px] font-heavy" style={{ color: stageColor(i, stages.length - 1) }}>
                    {pct}%
                  </p>
                  <p className="text-[10px] text-white/40">avg {fmtSeconds(avg)} in-stage</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Kanban — horizontal scroll on mobile, 11-column grid desktop */}
      <div className="overflow-x-auto pb-2" data-testid="pipeline-kanban">
        <div className="flex gap-3 min-w-max">
          {stages.map((s, i) => {
            const list = columns[s] || [];
            const color = s === "Closed Lost" ? "#6B7280" : stageColor(i, stages.length - 1);
            return (
              <div
                key={s}
                className="w-[240px] flex-shrink-0 rounded-sm border border-white/10 bg-black/50 flex flex-col max-h-[640px]"
                data-testid={`stage-col-${s.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="px-3 py-2.5 border-b border-white/10 flex items-center justify-between sticky top-0 bg-black/70 backdrop-blur z-10">
                  <p className="font-heavy text-[11px] tracking-tight" style={{ color }}>
                    {s.toUpperCase()}
                  </p>
                  <span className="font-heavy text-[14px]" style={{ color }}>
                    {counts[s] || 0}
                  </span>
                </div>
                <div className="overflow-y-auto divide-y divide-white/5">
                  {list.length === 0 && (
                    <p className="px-3 py-8 text-center text-white/30 text-[11px]">empty</p>
                  )}
                  {list.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => onPickLead && onPickLead(l.id)}
                      data-testid={`stage-card-${l.id.slice(0, 8)}`}
                      className="w-full text-left px-3 py-2.5 hover:bg-white/[0.04] block"
                    >
                      <p className="text-[12px] font-mono text-white truncate">
                        {l.email || l.phone || l.id.slice(0, 8)}
                      </p>
                      <p className="text-[10.5px] text-white/45 tracking-tight mt-0.5 truncate">
                        {l.first_name} {l.last_name} · {l.city || "—"}
                      </p>
                      {l.interest && (
                        <p className="text-[10px] text-white/35 mt-0.5 truncate">
                          {l.interest}
                        </p>
                      )}
                      {l.owner_email && (
                        <p className="text-[9.5px] text-white/30 mt-1 inline-flex items-center gap-1">
                          <Users size={9} /> {l.owner_email.split("@")[0]}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
